import { useEffect, useMemo, useState } from "react";
import { Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import { api, formatEUR } from "../lib/api";

const SIZE_FILTERS = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL"];
const SORTS = [
  { key: "featured", label: "Mis en avant" },
  { key: "price-asc", label: "Prix croissant" },
  { key: "price-desc", label: "Prix décroissant" },
  { key: "name", label: "Nom A–Z" },
];

const Shop = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("");
  const [size, setSize] = useState("");
  const [sort, setSort] = useState("featured");
  const [search, setSearch] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  useEffect(() => {
    api.get("/products").then((data) => { setProducts(data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const categories = useMemo(() => [...new Set(products.map((p) => p.category))], [products]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category) list = list.filter((p) => p.category === category);
    if (size) list = list.filter((p) => p.sizes.some((s) => s.size === size && s.stock > 0));
    if (maxPrice) list = list.filter((p) => p.price <= parseFloat(maxPrice));
    if (search) list = list.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => (b.featured - a.featured));
    return list;
  }, [products, category, size, maxPrice, search, sort]);

  return (
    <main className="pt-28 sm:pt-36 pb-24 px-4 sm:px-8 lg:px-16 min-h-screen" data-testid="shop-page">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">Catalogue</p>
        <h1 className="mt-3 font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-6xl text-white">
          La <span className="text-outline-strong">boutique</span>
        </h1>
      </Reveal>

      <div className="mt-10 flex flex-col lg:flex-row lg:items-center gap-4 border-y border-line py-4" data-testid="shop-filters">
        <input
          data-testid="shop-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher une pièce…"
          className="bg-transparent border border-line focus:border-white outline-none px-4 py-2.5 text-sm text-white placeholder:text-neutral-600 w-full lg:w-64 transition-colors"
        />
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="filter-category-all"
            onClick={() => setCategory("")}
            className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors ${!category ? "bg-white text-ink border-white" : "border-line text-neutral-400 hover:text-white"}`}
          >
            Tout
          </button>
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`filter-category-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setCategory(category === c ? "" : c)}
              className={`px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors ${category === c ? "bg-white text-ink border-white" : "border-line text-neutral-400 hover:text-white"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:ml-auto">
          {SIZE_FILTERS.map((s) => (
            <button
              key={s}
              data-testid={`filter-size-${s.toLowerCase()}`}
              onClick={() => setSize(size === s ? "" : s)}
              className={`w-10 h-9 font-mono text-xs border transition-colors ${size === s ? "bg-white text-ink border-white" : "border-line text-neutral-400 hover:text-white"}`}
            >
              {s}
            </button>
          ))}
          <div className="flex items-center border border-line px-3 py-1.5 gap-2">
            <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-neutral-500">Max</span>
            <input
              data-testid="filter-price-input"
              type="number"
              min="0"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="—"
              className="w-16 bg-transparent text-sm text-white outline-none font-mono"
            />
            <span className="font-mono text-[10px] text-neutral-500">€</span>
          </div>
          <select
            data-testid="shop-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-ink border border-line text-sm text-white px-3 py-2 outline-none focus:border-white"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="py-32 text-center font-mono text-xs uppercase tracking-[0.3em] text-neutral-500" data-testid="shop-loading">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="py-32 text-center" data-testid="shop-empty">
          <p className="font-display font-bold uppercase text-outline text-5xl">Aucune pièce</p>
          <p className="mt-4 text-sm text-neutral-400">Essayez d'élargir vos filtres.</p>
        </div>
      ) : (
        <>
          <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500" data-testid="shop-count">
            {filtered.length} pièce{filtered.length > 1 ? "s" : ""} — prix à partir de {formatEUR(Math.min(...filtered.map((p) => p.price)))}
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={Math.min(i, 6) * 0.05}>
                <ProductCard product={p} />
              </Reveal>
            ))}
          </div>
        </>
      )}
    </main>
  );
};

export default Shop;
